import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { PaginationBar } from "@/components/PaginationBar";
import { Download, Receipt } from "lucide-react";

export default function PortalCreditNotes() {
  const [d, setD] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/portal/finance/credit-notes", { params: { page, limit: 20 } })
      .then(({ data }) => setD(data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [page]);
  useEffect(load, [load]);

  const download = async (cnid, cnNo) => {
    try {
      const res = await api.get(`/portal/finance/credit-notes/${cnid}/pdf`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `credit-note-${cnNo || cnid}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {}
  };

  if (error) return <WidgetError error={error} onRetry={load}
                                 title="Could not load credit notes" testId="portal-cn-error"/>;

  return (
    <div data-testid="portal-credit-notes-page">
      <div className="flex items-center gap-2 mb-4">
        <Receipt size={20}/>
        <h1 className="text-3xl font-black tracking-tight">Credit Notes</h1>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">CN #</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>
            ) : d.items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-xs text-slate-500">No credit notes.</td></tr>
            ) : d.items.map((cn) => (
              <tr key={cn.id} className="border-b border-slate-100"
                  data-testid={`portal-cn-row-${cn.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{cn.credit_note_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{(cn.issued_at || cn.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs">{cn.reason}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtMoney(cn.total_cents)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => download(cn.id, cn.credit_note_no)}
                          data-testid={`portal-cn-download-${cn.id}`}
                          className="text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1">
                    <Download size={12}/> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={d.pages || 1} onChange={setPage}/>
    </div>
  );
}
